import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Uploader from '../components/Uploader';
import DropLinkManager from '../components/DropLinkManager';
import Modal from '../components/Modal';
import TripEditModal from '../components/TripEditModal';
import FrameGrabber from '../components/FrameGrabber';
import Globe from '../components/Globe';
import MovieCard from '../components/MovieCard';
import TripFeature from '../components/TripFeature';
import TripHighlights from '../components/TripHighlights';
import SurfConditions from '../components/SurfConditions';
import HoverVideo from '../components/HoverVideo';
import JobFanoutGraph from '../components/JobFanoutGraph';
import { estimateJobCost } from '../utils/cost';
import { movieTripIds } from './Movies';
import {
  useTrip,
  useTripAssets,
  useTripUploads,
  deleteTripUpload,
  useTripConditions,
  useMovies,
  renameAsset,
  trashAsset,
  restoreAsset,
  vaporizeAsset,
  trashTrip,
  restoreTrip,
  vaporizeTrip,
  requestTripConditions,
  saveManualConditions,
  setTripSurfSessions,
  requestProxy,
  isTrashed,
  purgeDaysLeft,
  shiftAssetsCapturedTime,
  clearAssetsCapturedTime,
  TRASH_RETENTION_DAYS,
} from '../data/firestore';
import { getAssetPlaybackUrl, downloadAsset, getAssetBackupStatus } from '../data/uploadService';
import { useAuth } from '../AuthContext';
import { assetTimeMs } from '../utils/conditions';
import { InlineRename, DownloadButton, TrashControls, VaporizeModal } from '../components/AssetActions';
import AssetDetails from '../components/AssetDetails';
import { httpsify } from '../utils/url';
import { tripMonthlyStorage, fmtUSD } from '../utils/cost';
import { fmtDuration, useNow, renderEtaLabel, renderJobLabel, assetRenderJob } from '../utils/eta';
import { mapLinks } from '../utils/geo';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};

const tsMillis = (ts) =>
  ts?.toMillis ? ts.toMillis() : ts?.seconds ? ts.seconds * 1000 : 0;

const dayLabelFromMs = (ms) => {
  if (!ms) return 'Undated';
  return new Date(ms).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
// Best capture instant for grouping: a manual override or the probed recording
// date, falling back to the upload time so nothing goes missing.
const captureMs = (a) => {
  const t = assetTimeMs(a);
  if (Number.isFinite(t)) return t;
  return tsMillis(a.uploadedAt);
};

// Live "uploading now" cards for in-flight guest (drop-link) uploads. The
// backend mirrors each guest's progress heartbeats into trips/{id}/uploads
// (monotonic, terminal-safe), so the owner watches a contributor's upload fill
// in before the finished file lands as a real asset.
//
// Heartbeats are throttled and depend on the guest's link, so a healthy upload
// can legitimately go quiet for a while (a single 100MB part on a slow uplink
// takes minutes). We give uploads lots of breathing room before easing the
// label from "live" → "waiting…" → "stalled".
//
// Critically, a stalled upload is NOT abandoned: its multipart parts are saved
// on S3 and fully resumable. So instead of hiding it, we keep the row visible
// (flagged "stalled") so the owner knows it needs a nudge — the contributor just
// has to reopen the link to pick up where it left off. We only drop a row once
// it's been silent for a very long time (likely truly given up).
const UPLOAD_LIVE_MS = 60 * 1000; // updated within → "live"
const UPLOAD_WAIT_MS = 5 * 60 * 1000; // 60s–5min of quiet → "waiting…"
const UPLOAD_ABANDON_MS = 24 * 60 * 60 * 1000; // silent this long → finally hide
const DONE_LINGER_MS = 5 * 1000; // show a completed row's ✓ briefly, then auto-dismiss

// Map a heartbeat doc + current clock to a display phase.
function uploadPhase(u, nowMs) {
  if (u.status === 'error') return 'error';
  if (u.status === 'done') return 'done';
  const age = nowMs - (u.updatedMs || 0);
  if (age > UPLOAD_WAIT_MS) return 'stalled';
  if (age > UPLOAD_LIVE_MS) return 'waiting';
  return 'live';
}

// Camera-clock corrector. Cameras sometimes record with the wrong clock or
// timezone (e.g. a contributor's body set to the wrong time), so the recorded
// timestamp doesn't match the real surf conditions. This shifts the capture
// time of a set of files by a chosen number of hours (stored as overrides), and
// can revert to the probed recording date.
function ClockFix({ tripId, assets, scopeLabel }) {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const n = assets.length;
  const datedCount = assets.filter((a) => Number.isFinite(assetTimeMs(a))).length;

  const round2 = (h) => Math.round(h * 100) / 100;
  const apply = async () => {
    if (!hours) return;
    setBusy(true);
    setMsg('');
    try {
      const c = await shiftAssetsCapturedTime(tripId, assets, hours);
      setMsg(`Shifted ${c} file${c === 1 ? '' : 's'} by ${hours > 0 ? '+' : ''}${round2(hours)}h.`);
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    setBusy(true);
    setMsg('');
    try {
      const c = await clearAssetsCapturedTime(tripId, assets);
      setMsg(`Reverted ${c} file${c === 1 ? '' : 's'} to their recorded time.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cs-clockfix">
      <button
        className={`cs-btn cs-btn-sm ${open ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
        onClick={() => setOpen((o) => !o)}
        title="Correct a wrong camera clock by shifting capture times to match conditions"
      >
        ⏱ Fix clock
      </button>
      {open && (
        <div className="cs-clockfix-pop">
          <div className="cs-help" style={{ margin: 0 }}>
            Shift the capture time of <strong>{scopeLabel}</strong> ({datedCount} of {n} dated) so it
            lines up with swell, wind &amp; tide. Use this when a camera’s clock or timezone was off.
          </div>
          <div className="cs-clockfix-row">
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setHours((h) => round2(h - 1))}>−1h</button>
            <input
              className="cs-input cs-input-sm"
              type="number"
              step="0.5"
              value={hours}
              onChange={(e) => setHours(Number(e.target.value) || 0)}
              style={{ width: 84, textAlign: 'center' }}
            />
            <span className="cs-help" style={{ margin: 0 }}>hours</span>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setHours((h) => round2(h + 1))}>+1h</button>
          </div>
          <div className="cs-clockfix-row">
            <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || !hours} onClick={apply}>
              {busy ? 'Applying…' : `Apply ${hours > 0 ? '+' : ''}${round2(hours)}h`}
            </button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={reset} title="Remove manual shifts on these files">
              Reset to recorded
            </button>
          </div>
          {msg && <div className="cs-help" style={{ margin: 0, color: 'var(--surf)' }}>{msg}</div>}
        </div>
      )}
    </div>
  );
}

function UploadingNow({ tripId, assets }) {
  const { docs } = useTripUploads(tripId);
  const tick = useNow(true); // re-render so freshness is re-evaluated live
  const dismissTimers = useRef(new Map());

  // The real source of truth for "finished" is the recorded asset, not the
  // heartbeat. A final "done" beat can be lost (a quota blip, the tab closing,
  // throttling), which would otherwise leave a completed upload frozen as a
  // "stalled" card forever. So whenever a live card's file (name + size) already
  // exists as a recorded asset, we know it landed — retire the card.
  const recordedSigs = useMemo(() => {
    const s = new Set();
    (assets || []).forEach((a) => {
      if (isTrashed(a)) return;
      s.add(`${a.name}\u0000${a.sizeBytes || 0}`);
    });
    return s;
  }, [assets]);
  const isRecorded = (u) => recordedSigs.has(`${u.name}\u0000${u.sizeBytes || 0}`);

  // Auto-dismiss completed rows: explicit "done" beats flash briefly, and any
  // row whose file is already a recorded asset is cleaned up (its done beat was
  // lost). The finished file is in the grid, so the live card is redundant.
  useEffect(() => {
    const timers = dismissTimers.current;
    (docs || []).forEach((u) => {
      if (timers.has(u.id)) return;
      const done = u.status === 'done';
      const landed = isRecorded(u);
      if (!done && !landed) return;
      const delay = done
        ? Math.max(0, DONE_LINGER_MS - (Date.now() - (u.doneMs || u.updatedMs || 0)))
        : 0;
      timers.set(
        u.id,
        setTimeout(() => {
          deleteTripUpload(tripId, u.id);
          timers.delete(u.id);
        }, delay)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, tripId, recordedSigs]);
  useEffect(() => {
    const timers = dismissTimers.current;
    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  const live = useMemo(() => {
    const nowMs = tick || Date.now();
    const visible = (docs || []).filter((u) => {
      // Already a recorded asset → finished; never show it as in-flight.
      if (isRecorded(u)) return false;
      if (u.status === 'error') return true;
      if (u.status === 'done') return nowMs - (u.doneMs || u.updatedMs || 0) < DONE_LINGER_MS + 1500;
      return nowMs - (u.updatedMs || 0) < UPLOAD_ABANDON_MS;
    });
    // A contributor who reopens the link and re-drops the same file can spawn a
    // second heartbeat doc (e.g. a re-downloaded copy with a new mtime → new id).
    // Collapse those into one card per file (name + size), keeping the most
    // representative beat: done > furthest along > most recent.
    const rank = (u) => (u.status === 'done' ? 2 : u.status === 'error' ? 0 : 1);
    const better = (a, b) => {
      if (!a) return b;
      if (rank(b) !== rank(a)) return rank(b) > rank(a) ? b : a;
      if ((b.loaded || 0) !== (a.loaded || 0)) return (b.loaded || 0) > (a.loaded || 0) ? b : a;
      return (b.updatedMs || 0) > (a.updatedMs || 0) ? b : a;
    };
    const byFile = new Map();
    for (const u of visible) {
      const k = `${u.name}\u0000${u.sizeBytes || 0}`;
      byFile.set(k, better(byFile.get(k), u));
    }
    return [...byFile.values()].sort((a, b) => (b.updatedMs || 0) - (a.updatedMs || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, tick, recordedSigs]);
  if (!live.length) return null;

  const nowMs = tick || Date.now();
  const anyLive = live.some((u) => uploadPhase(u, nowMs) === 'live');
  const stalledCount = live.filter((u) => uploadPhase(u, nowMs) === 'stalled').length;

  return (
    <section style={{ marginTop: 16 }}>
      <h3 style={{ fontSize: 15, color: 'var(--surf)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="cs-live-dot" /> {anyLive ? 'Uploading now' : 'Incoming uploads'} · {live.length}
        {stalledCount > 0 && (
          <span style={{ fontSize: 12, fontWeight: 500, color: '#e0a35f' }}>
            · {stalledCount} stalled (reopen the link to resume)
          </span>
        )}
      </h3>
      <div className="cs-grid">
        {live.map((u) => {
          const phase = uploadPhase(u, nowMs);
          const pct = u.status === 'done' ? 100 : Math.max(0, Math.min(100, u.pct || 0));
          const tone =
            phase === 'error'
              ? '#e0645f'
              : phase === 'done'
              ? '#5fcf8f'
              : phase === 'stalled'
              ? '#e0a35f'
              : phase === 'waiting'
              ? 'var(--muted)'
              : 'var(--surf)';
          const label =
            phase === 'error'
              ? '⚠ error'
              : phase === 'done'
              ? '✓ done'
              : phase === 'stalled'
              ? '⏸ stalled'
              : phase === 'waiting'
              ? 'waiting…'
              : 'live';
          return (
            <div className="cs-trip-card cs-upcard" key={u.id}>
              <div className="cs-trip-cover cs-upcard-cover">
                <div className="cs-upcard-icon">{u.isFolder ? '🎞' : u.kind === 'photo' ? '🖼' : '▶'}</div>
                <button
                  type="button"
                  className="cs-upcard-x"
                  title="Dismiss this row"
                  onClick={() => deleteTripUpload(tripId, u.id)}
                >
                  ×
                </button>
              </div>
              <div className="cs-trip-body">
                <div className="cs-upcard-name" title={u.name}>{u.name}</div>
                {u.contributor && (
                  <div className="cs-by" title={`Uploading: ${u.contributor}`}>
                    <span className="cs-by-dot" /> {u.contributor}
                  </div>
                )}
                <div className="cs-upbar">
                  <div
                    className={`cs-upbar-fill ${phase === 'error' ? 'err' : ''}`}
                    style={{ width: `${phase === 'error' ? 100 : pct}%` }}
                  />
                </div>
                <div className="meta" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span>
                    {phase === 'error'
                      ? 'Upload error'
                      : `${fmtBytes(u.loaded || 0)} / ${fmtBytes(u.sizeBytes || 0)} · ${pct}%`}
                    {u.isFolder && u.fileCount ? ` · ${u.fileCount} files` : ''}
                  </span>
                  <span style={{ marginLeft: 'auto', color: tone }}>{label}</span>
                </div>
                {phase === 'stalled' && (
                  <div className="meta" style={{ color: '#e0a35f', marginTop: 2 }}>
                    paused — saved parts are kept; reopen the link to resume
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Lazily presign a photo so we can show a thumbnail from the private bucket.
// RAW assets use their developed thumbnail (the raw file isn't viewable).
function PhotoThumb({ asset, tripId, onOpen, onEdit, cond, selected, onSelect, canEdit }) {
  const [url, setUrl] = useState('');
  const trashed = isTrashed(asset);
  const contributor = asset.photographer || asset.lineage?.contributor || asset.contributor || '';
  // A baking still / develop / grade export shows a live ETA + estimated cost on
  // the card. Derived from the asset's in-flight sub-status (works regardless of
  // whether the worker has stamped the enriched renderJob yet).
  const rj = assetRenderJob(asset);
  const rjBaking = !!rj;
  const rjNow = useNow(rjBaking);
  const rjCostUsd = rj?.costUsd != null ? rj.costUsd : rj?.costEstUsd;
  const cardTags = Array.isArray(asset.tags) ? asset.tags.filter(Boolean) : [];
  useEffect(() => {
    let on = true;
    if (asset.thumbUrl) {
      setUrl(asset.thumbUrl);
    } else if (asset.s3Key && !asset.isRaw) {
      getAssetPlaybackUrl(asset.s3Key).then((u) => on && setUrl(u)).catch(() => {});
    }
    return () => {
      on = false;
    };
  }, [asset.s3Key, asset.thumbUrl, asset.isRaw]);

  // Open at the highest available resolution rather than the 640px card thumb,
  // so zooming a grabbed RED still / photo stays sharp. Plain photos & grabbed
  // stills presign the original; RAW falls back to its developed/exported JPEG.
  const openFull = async () => {
    if (trashed) return;
    // Prefer a graded full-res display (RED negatives keep their look here, not in
    // the master; any exported photo gets one too). Otherwise the presigned
    // original (sharpest for plain photos), then RAW develop/export fallbacks.
    if (asset.displayUrl) {
      onOpen(httpsify(asset.displayUrl));
      return;
    }
    if (!asset.isRaw && asset.s3Key) {
      try {
        const hi = await getAssetPlaybackUrl(asset.s3Key);
        onOpen(hi || url);
        return;
      } catch (_e) {
        /* fall back to the card image */
      }
    } else if (asset.isRaw && asset.derivedUrl) {
      onOpen(asset.derivedUrl);
      return;
    }
    onOpen(url);
  };

  return (
    <div
      className={`cs-trip-card ${trashed ? 'cs-trashed' : ''} ${selected ? 'cs-card-sel' : ''}`}
      style={{ cursor: trashed ? 'default' : 'pointer', position: 'relative' }}
      onClick={openFull}
    >
      {asset.isRaw && <span className="cs-raw-badge">RAW</span>}
      {asset.isRaw && asset.develop?.status && asset.develop.status !== 'ready' && (
        <span className="cs-develop-badge">{asset.develop.status === 'error' ? 'develop failed' : 'developing…'}</span>
      )}
      <div
        className="cs-trip-cover"
        style={url ? { backgroundImage: `url(${url})` } : { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}
      >
        {!url && (rjBaking ? '⏳' : '🖼')}
      </div>
      <div className="cs-trip-body">
        {canEdit ? (
          <InlineRename
            name={asset.name}
            onSave={(n) => renameAsset(tripId, asset.id, n)}
            className="cs-card-title"
            style={{ fontSize: 14 }}
          />
        ) : (
          <div style={{ fontSize: 14, fontWeight: 600 }}>{asset.name}</div>
        )}
        {(contributor || asset.surfer) && (
          <div className="cs-by" title={[contributor && `Photographer: ${contributor}`, asset.surfer && `Surfer: ${asset.surfer}`].filter(Boolean).join(' · ')}>
            {contributor && <><span className="cs-by-dot" /> by {contributor}</>}
            {asset.surfer && <span className="cs-by-surfer">🏄 {asset.surfer}</span>}
          </div>
        )}
        {rjBaking && (
          <div className="cs-card-status">
            <span className="cs-card-status-msg">{renderJobLabel(rj, rjNow)}</span>
            <div className="cs-card-status-ctl">
              <span className="cs-card-cost" title="Estimated compute cost to bake this still (metered by wallclock when done)">
                ~{fmtUSD(rjCostUsd || 0)} est
              </span>
            </div>
          </div>
        )}
        <div className="meta" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span>{fmtBytes(asset.sizeBytes)} · {asset.isRaw ? `RAW ${asset.rawFormat || ''}`.trim() : 'photo'}</span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            {trashed ? (
              canEdit && (
                <TrashControls
                  trashed
                  daysLeft={purgeDaysLeft(asset)}
                  what={`the photo “${asset.name}”`}
                  onRestore={() => restoreAsset(tripId, asset.id)}
                  onVaporize={() => vaporizeAsset(tripId, asset)}
                />
              )
            ) : (
              <>
                {cond && onSelect && (
                  <button
                    className={`cs-btn cs-btn-sm ${selected ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                    onClick={(e) => { e.stopPropagation(); onSelect(); }}
                    title="Show this photo's swell, wind & tide"
                  >
                    🌊
                  </button>
                )}
                {canEdit && (
                  <>
                    <DownloadButton s3Key={asset.s3Key} name={asset.name} label="⤓" title="Download original" />
                    <button
                      className="cs-btn cs-btn-ghost cs-btn-sm"
                      onClick={(e) => { e.stopPropagation(); onEdit(); }}
                      title="Edit color & light (exposure, white balance, RED IPP2, grading)"
                    >
                      🎨 Color
                    </button>
                    <TrashControls what={`the photo “${asset.name}”`} onTrash={() => trashAsset(tripId, asset.id)} />
                  </>
                )}
              </>
            )}
          </span>
        </div>
        {cardTags.length > 0 && (
          <div className="cs-card-tags" title={cardTags.join(' · ')}>
            {cardTags.map((t) => (
              <span key={t} className="cs-card-tag">{t}</span>
            ))}
          </div>
        )}
        {!trashed && <AssetDetails asset={asset} tripId={tripId} cond={cond} />}
      </div>
    </div>
  );
}

// Off-site safety indicator for a raw original. The raw bucket is versioned +
// delete-protected and auto-replicated to a Glacier backup, so this asset can't
// be lost or permanently deleted. Confirms the Glacier copy exists and offers a
// download straight from the backup. Lazily HEAD-checks once mounted.
function BackupChip({ asset }) {
  const [status, setStatus] = useState(null); // null=loading | {allBackedUp, parts}
  const [dl, setDl] = useState(false);
  const parts = Array.isArray(asset.parts) && asset.parts.length
    ? asset.parts
    : asset.s3Key ? [{ s3Key: asset.s3Key, name: asset.name }] : [];
  useEffect(() => {
    let on = true;
    if (!parts.length) { setStatus({ allBackedUp: false, parts: [] }); return undefined; }
    getAssetBackupStatus({ key: asset.s3Key, parts })
      .then((s) => on && setStatus(s))
      .catch(() => on && setStatus({ error: true }));
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.s3Key]);

  if (!parts.length) return null;

  const downloadGlacier = async (e) => {
    e.stopPropagation();
    if (dl) return;
    setDl(true);
    try {
      for (let i = 0; i < parts.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await downloadAsset({ key: parts[i].s3Key, name: parts[i].name, bucket: 'backup' });
        // eslint-disable-next-line no-await-in-loop
        if (i < parts.length - 1) await new Promise((r) => setTimeout(r, 800));
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(`Glacier download failed: ${err.message || err}`);
    } finally {
      setDl(false);
    }
  };

  const backedUp = status && status.allBackedUp;
  const loading = status === null;
  const lockTitle = backedUp
    ? 'Protected: original is versioned, delete-protected, and mirrored off-site to a Glacier backup. It can’t be lost or permanently deleted.'
    : loading
    ? 'Checking off-site backup…'
    : 'Original is versioned & delete-protected. Off-site Glacier copy is still syncing.';

  return (
    <span className="cs-backup-chip" onClick={(e) => e.stopPropagation()} role="presentation">
      <span
        className={`cs-lock ${backedUp ? 'ok' : loading ? 'wait' : 'partial'}`}
        title={lockTitle}
      >
        {loading ? '🔒…' : backedUp ? '🔒 Backed up' : '🔒 Protected'}
      </span>
      {backedUp && (
        <button
          type="button"
          className="cs-btn cs-btn-ghost cs-btn-sm"
          title="Download the original from the off-site Glacier backup"
          disabled={dl}
          onClick={downloadGlacier}
        >
          {dl ? '…' : '❄ Glacier'}
        </button>
      )}
    </span>
  );
}

function VideoCard({ asset, tripId, onGrab, cond, selected, onSelect, canEdit }) {
  const navigate = useNavigate();
  const [showJob, setShowJob] = useState(false);
  const trashed = isTrashed(asset);
  const contributor = asset.photographer || asset.lineage?.contributor || asset.contributor || '';
  const isRed = !!asset.isRawVideo;
  const proxyStatus = asset.proxy?.status;
  const proxyReady = proxyStatus === 'ready';
  const proxyBusy = proxyStatus === 'queued' || proxyStatus === 'rendering';
  const proxyErr = proxyStatus === 'error';
  const now = useNow(proxyBusy);
  // Offer a manual re-queue if a decode looks wedged (running >25 min with no
  // finish). The worker also self-heals orphans on restart, so this is a backup.
  const proxyStuck =
    proxyStatus === 'rendering' &&
    asset.proxy?.startedAt &&
    now - asset.proxy.startedAt > 25 * 60 * 1000;
  // RED clips are only watchable once the proxy is decoded.
  const playable = !isRed || proxyReady;
  const go = () => !trashed && playable && navigate(`/watch/asset/${tripId}/${asset.id}`);
  // Lazy hover-preview source: RED plays its CDN HLS proxy; others stream the
  // presigned MP4. Resolved only on sustained hover.
  const resolvePreview = useCallback(async () => {
    if (isRed) return proxyReady && asset.proxyHlsUrl ? { url: httpsify(asset.proxyHlsUrl), hls: true } : null;
    // Non-RED: prefer the lightweight 1080p HLS proxy (snappy hover scrub) when
    // it's been built; otherwise stream the presigned original.
    if (asset.proxyHlsUrl) return { url: httpsify(asset.proxyHlsUrl), hls: true };
    if (!asset.s3Key) return null;
    const u = await getAssetPlaybackUrl(asset.s3Key);
    return u ? { url: u, hls: false } : null;
  }, [isRed, proxyReady, asset.proxyHlsUrl, asset.s3Key]);
  return (
    <div className={`cs-trip-card ${trashed ? 'cs-trashed' : ''} ${selected ? 'cs-card-sel' : ''}`} style={{ position: 'relative' }}>
      {isRed && <span className="cs-red-badge">RED R3D</span>}
      {asset.isProxy && (
        <span className="cs-proxy-badge" title="Lightweight streaming proxy of the original master">
          {asset.proxyH ? `${asset.proxyH}p` : '1080p'} PROXY
        </span>
      )}
      <div
        role="button"
        tabIndex={0}
        style={{ cursor: trashed || !playable ? 'default' : 'pointer' }}
        onClick={go}
        onKeyDown={(e) => e.key === 'Enter' && go()}
      >
        <HoverVideo
          className="cs-trip-cover"
          resolve={resolvePreview}
          disabled={trashed || !playable}
          style={
            asset.thumbUrl
              ? { backgroundImage: `url(${httpsify(asset.thumbUrl)})` }
              : { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }
          }
        >
          {!asset.thumbUrl &&
            (proxyBusy || asset.thumb?.status === 'rendering' || asset.thumb?.status === 'queued'
              ? '⏳'
              : proxyErr
              ? '⚠'
              : '▶')}
        </HoverVideo>
      </div>
      <div className="cs-trip-body">
        {canEdit ? (
          <InlineRename
            name={asset.name}
            onSave={(n) => renameAsset(tripId, asset.id, n)}
            className="cs-card-title"
            style={{ fontSize: 14 }}
          />
        ) : (
          <div style={{ fontSize: 14, fontWeight: 600 }}>{asset.name}</div>
        )}
        {(contributor || asset.surfer) && (
          <div className="cs-by" title={[contributor && `Photographer: ${contributor}`, asset.surfer && `Surfer: ${asset.surfer}`].filter(Boolean).join(' · ')}>
            {contributor && <><span className="cs-by-dot" /> by {contributor}</>}
            {asset.surfer && <span className="cs-by-surfer">🏄 {asset.surfer}</span>}
          </div>
        )}
        {isRed && !proxyReady && (
          <div className="cs-card-status">
            <span className={`cs-card-status-msg ${proxyErr ? 'is-err' : ''}`}>
              {proxyBusy
                ? renderEtaLabel(asset.proxy, now, 'Decoding RED')
                : proxyErr
                ? `Proxy failed: ${asset.proxy?.message || 'unknown error'}`
                : 'Stream proxy pending'}
            </span>
            <div className="cs-card-status-ctl">
              <span className="cs-card-cost" title="Estimated ephemeral decode cost (billed per-second while running)">
                ~{fmtUSD(estimateJobCost({ durationSec: asset.durationSec || 0, srcFps: asset.srcFps || 0, sizeBytes: asset.sizeBytes || 0 }).usd)} est
              </span>
              {(proxyErr || proxyStuck) && (
                <button
                  type="button"
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  title="Re-queue this clip for decoding"
                  onClick={(e) => {
                    e.stopPropagation();
                    requestProxy(tripId, asset.id);
                  }}
                >
                  ↻ Retry decode
                </button>
              )}
              {proxyBusy && asset.proxy?.jobId && (
                <button
                  type="button"
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  title="Show the parallel decode tasks fanning out"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowJob((v) => !v);
                  }}
                >
                  {showJob ? '▾ Hide infra' : '⚡ Show infra'}
                </button>
              )}
            </div>
          </div>
        )}
        {isRed && proxyBusy && asset.proxy?.jobId && showJob && (
          <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()} role="presentation">
            <JobFanoutGraph jobId={asset.proxy.jobId} height={300} title="RED decode · fan-out" />
          </div>
        )}
        <div className="meta cs-card-foot">
          <span className="cs-card-size">{fmtBytes(asset.sizeBytes)}</span>
          <span className="cs-card-actions">
            {trashed ? (
              canEdit && (
                <TrashControls
                  trashed
                  daysLeft={purgeDaysLeft(asset)}
                  what={`the clip “${asset.name}”`}
                  onRestore={() => restoreAsset(tripId, asset.id)}
                  onVaporize={() => vaporizeAsset(tripId, asset)}
                />
              )
            ) : (
              <>
                {cond && onSelect && (
                  <button
                    className={`cs-btn cs-btn-sm ${selected ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                    onClick={(e) => { e.stopPropagation(); onSelect(); }}
                    title="Show this clip's swell, wind & tide"
                  >
                    🌊
                  </button>
                )}
                {canEdit && (
                  <>
                    <BackupChip asset={asset} />
                    <DownloadButton
                      s3Key={asset.s3Key}
                      name={asset.name}
                      parts={asset.parts}
                      label="⤓"
                      title="Download original"
                    />
                    {(!isRed || proxyReady) && (
                      <button
                        className="cs-btn cs-btn-ghost cs-btn-sm"
                        title="Save a full-resolution still (with this clip's look) to the trip's photos"
                        onClick={(e) => {
                          e.stopPropagation();
                          onGrab(asset);
                        }}
                      >
                        🖼 Grab frame
                      </button>
                    )}
                    <TrashControls what={`the clip “${asset.name}”`} onTrash={() => trashAsset(tripId, asset.id)} />
                  </>
                )}
              </>
            )}
          </span>
        </div>
        {!trashed && <AssetDetails asset={asset} tripId={tripId} cond={cond} />}
      </div>
    </div>
  );
}

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = !!user;
  const { doc: trip } = useTrip(id);
  const { docs: assets } = useTripAssets(id);
  const { doc: cond } = useTripConditions(id);
  const { docs: movies } = useMovies();
  const [lightbox, setLightbox] = useState(null);
  const [editing, setEditing] = useState(false);
  const [grabAsset, setGrabAsset] = useState(null);
  const [group, setGroup] = useState('day'); // 'day' | 'type'
  const [contribFilter, setContribFilter] = useState('all'); // 'all' | '__none' | <name>
  const [surferFilter, setSurferFilter] = useState('all'); // 'all' | '__none' | <name>
  const [dlAll, setDlAll] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [vaporizeTripOpen, setVaporizeTripOpen] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const [magEditing, setMagEditing] = useState(false); // curating the feature board
  const [hlEditing, setHlEditing] = useState(false); // curating the highlights strip
  const [selId, setSelId] = useState(null); // asset whose conditions are pinned
  const condRef = useRef(null);
  const hlStatus = trip?.highlight?.status;
  const now = useNow(hlStatus === 'rendering' || hlStatus === 'queued');

  // Live assets vs. soft-deleted ones (kept around for recovery).
  const { active, trashed } = useMemo(() => {
    const a = [];
    const t = [];
    (assets || []).forEach((x) => (isTrashed(x) ? t : a).push(x));
    return { active: a, trashed: t };
  }, [assets]);

  // People credited in this trip's media (for the two filter dropdowns). The
  // owner's manual `photographer` override wins over the upload-time name; the
  // surfer tag is owner-set only.
  const photographerOf = (a) => a.photographer || a.lineage?.contributor || a.contributor || '';
  const surferOf = (a) => a.surfer || '';
  // Within a trip, the same first name almost always means the same person, so
  // we group names case-insensitively (and trimmed): "tucker", "Tucker " and
  // "Tucker" collapse to a single entry. If two people genuinely share a first
  // name, the owner separates them by adding a last name (case alone won't, since
  // we fold case on purpose to kill accidental duplicates).
  const normName = (s) => String(s || '').trim().toLowerCase();
  // normalized key -> most common spelling, sorted alphabetically.
  const nameOptions = (items, getName) => {
    const groups = new Map();
    items.forEach((a) => {
      const raw = getName(a).trim();
      if (!raw) return;
      const k = normName(raw);
      if (!groups.has(k)) groups.set(k, new Map());
      const variants = groups.get(k);
      variants.set(raw, (variants.get(raw) || 0) + 1);
    });
    return [...groups.entries()]
      .map(([key, variants]) => {
        // Representative spelling: most frequent, then prefer a leading capital.
        const label = [...variants.entries()].sort((x, y) => {
          if (y[1] !== x[1]) return y[1] - x[1];
          const cap = (s) => (/^[A-Z]/.test(s) ? 0 : 1);
          if (cap(x[0]) !== cap(y[0])) return cap(x[0]) - cap(y[0]);
          return x[0].localeCompare(y[0]);
        })[0][0];
        return { key, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  };
  const photographers = useMemo(() => nameOptions(active, photographerOf), [active]);
  const surfers = useMemo(() => nameOptions(active, surferOf), [active]);
  const hasUntagged = useMemo(() => active.some((a) => !photographerOf(a).trim()), [active]);
  const hasUntaggedSurfer = useMemo(() => active.some((a) => !surferOf(a).trim()), [active]);
  const selectedPhotographer = photographers.find((p) => p.key === contribFilter)?.label || contribFilter;
  const selectedSurfer = surfers.find((p) => p.key === surferFilter)?.label || surferFilter;
  // Reset a filter if its selected person no longer has any media.
  useEffect(() => {
    if (contribFilter === 'all' || contribFilter === '__none') return;
    if (!photographers.some((p) => p.key === contribFilter)) setContribFilter('all');
  }, [photographers, contribFilter]);
  useEffect(() => {
    if (surferFilter === 'all' || surferFilter === '__none') return;
    if (!surfers.some((p) => p.key === surferFilter)) setSurferFilter('all');
  }, [surfers, surferFilter]);

  // Active media after applying both filters (photographer AND surfer) —
  // everything downstream (day/type grouping, counts) is derived from this so
  // the filters are global. Selected values are normalized keys, so matching
  // is case-insensitive too.
  const visible = useMemo(() => {
    let out = active;
    if (contribFilter === '__none') out = out.filter((a) => !photographerOf(a).trim());
    else if (contribFilter !== 'all') out = out.filter((a) => normName(photographerOf(a)) === contribFilter);
    if (surferFilter === '__none') out = out.filter((a) => !surferOf(a).trim());
    else if (surferFilter !== 'all') out = out.filter((a) => normName(surferOf(a)) === surferFilter);
    return out;
  }, [active, contribFilter, surferFilter]);

  const { videos, photos } = useMemo(() => {
    const v = [];
    const p = [];
    visible.forEach((a) => (a.kind === 'photo' ? p : v).push(a));
    return { videos: v, photos: p };
  }, [visible]);

  const selectedAsset = useMemo(() => active.find((a) => a.id === selId) || null, [active, selId]);
  const condStatus = trip?.conditions?.status;
  // Select an asset for the conditions readout and bring the panel into view
  // (it lives near the top, so a card deep in the list scrolls up to it).
  const selectForConditions = (a) => {
    setSelId(a.id);
    setTimeout(() => condRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  // Group by the day the media was *shot* (recording date / manual override),
  // not when it was uploaded — sections run chronologically through the trip,
  // with any undated files collected at the end.
  const days = useMemo(() => {
    const map = new Map();
    visible.forEach((a) => {
      const ms = captureMs(a);
      const key = dayLabelFromMs(ms);
      if (!map.has(key)) map.set(key, { ms: ms || 0, items: [] });
      map.get(key).items.push(a);
    });
    return Array.from(map.entries())
      .sort((x, y) => (x[1].ms || Infinity) - (y[1].ms || Infinity))
      .map(([label, v]) => {
        const items = v.items
          .slice()
          .sort((p, q) => (captureMs(p) || 0) - (captureMs(q) || 0));
        return [label, items];
      });
  }, [visible]);

  const tripMovies = useMemo(
    () => (movies || []).filter((m) => movieTripIds(m).has(id)),
    [movies, id]
  );

  // Download every asset in the trip. S3 has no native "zip a folder", and
  // these files are huge, so we stream each object individually with a small
  // stagger to avoid the browser dropping concurrent download prompts.
  const downloadAll = async () => {
    const items = active.filter((a) => a.s3Key);
    if (!items.length) return;
    if (!window.confirm(`Start ${items.length} downloads? Allow multiple downloads if your browser asks.`)) {
      return;
    }
    setDlAll(true);
    try {
      for (const a of items) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await downloadAsset({ key: a.s3Key, name: a.name, bucket: 'raw' });
        } catch {
          /* keep going on individual failures */
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 900));
      }
    } finally {
      setDlAll(false);
    }
  };

  if (trip === undefined) return <div className="cs-page">Loading…</div>;
  if (trip === null) return <div className="cs-page">Trip not found.</div>;

  const links = mapLinks(trip.lat, trip.lng);

  const renderAsset = (a) =>
    a.kind === 'photo' ? (
      <PhotoThumb
        key={a.id}
        asset={a}
        tripId={id}
        onOpen={(u) => u && setLightbox(u)}
        onEdit={() => navigate(`/photo/${id}/${a.id}`)}
        cond={cond}
        selected={selId === a.id}
        onSelect={() => selectForConditions(a)}
        canEdit={canEdit}
      />
    ) : (
      <VideoCard
        key={a.id}
        asset={a}
        tripId={id}
        onGrab={setGrabAsset}
        cond={cond}
        selected={selId === a.id}
        onSelect={() => selectForConditions(a)}
        canEdit={canEdit}
      />
    );

  return (
    <div className="cs-page cs-trip-page">
      {canEdit && isTrashed(trip) && (
        <div className="cs-trash-banner">
          <span>
            🗑 This trip is in the trash — it (and all its media) will be permanently
            deleted in {purgeDaysLeft(trip)} days.
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => restoreTrip(trip.id)}>
              Restore trip
            </button>
            <button className="cs-btn cs-btn-danger cs-btn-sm" onClick={() => setVaporizeTripOpen(true)}>
              Vaporize now
            </button>
          </span>
        </div>
      )}

      <div className="cs-trip-overview">
      <div className="cs-trip-hero">
        {Number.isFinite(trip.lat) ? (
          <div className="cs-trip-hero-map">
            <Globe lat={trip.lat} lng={trip.lng} size={132} zoom={3} dot />
            {links && (
              <div className="cs-tripcard-links cs-trip-hero-maplinks">
                <a href={links.earth} target="_blank" rel="noreferrer">🌍 Earth</a>
                <a href={links.maps} target="_blank" rel="noreferrer">🛰 Satellite</a>
              </div>
            )}
          </div>
        ) : null}

        <div className="cs-trip-hero-info">
          <h1>{trip.name}</h1>
          <div className="cs-hero-meta">
            {[trip.location, trip.startDate, trip.endDate].filter(Boolean).join(' · ')}
          </div>
          <div className="cs-trip-stats">
            <span className="cs-badge">{videos.length} clips</span>
            <span className="cs-badge">{photos.length} photos</span>
            {tripMovies.length > 0 && <span className="cs-badge">{tripMovies.length} movies</span>}
            <span className="cs-badge" title="Total stored for this trip">{fmtBytes(trip.totalBytes)}</span>
            <span className="cs-badge" title="Estimated monthly S3 storage for this trip">
              ~{fmtUSD(tripMonthlyStorage(trip))}/mo
            </span>
            {(hlStatus === 'rendering' || hlStatus === 'queued') && (
              <span className="cs-badge">
                {hlStatus === 'queued'
                  ? 'highlight queued…'
                  : `rendering highlight… ${fmtDuration((now - (trip.highlight?.startedAt || now)) / 1000)}`}
              </span>
            )}
          </div>
          {trip.description && <p className="cs-trip-hero-desc">{trip.description}</p>}
          {canEdit && <div className="cs-trip-hero-s3">s3://{trip.s3Bucket}/{trip.s3Prefix}</div>}
        </div>

        {canEdit && (
          <div className="cs-trip-hero-actions">
            <button
              className={`cs-btn cs-btn-sm ${showUpload ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => setShowUpload((s) => !s)}
            >
              ⬆ Upload footage
            </button>
            <button
              className={`cs-btn cs-btn-sm ${showDrop ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => setShowDrop((s) => !s)}
            >
              🔗 Share upload link
            </button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setEditing(true)}>
              ✎ Edit trip
            </button>
            {active.length > 0 && (
              <button
                className={`cs-btn cs-btn-sm ${magEditing ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                onClick={() => setMagEditing((e) => !e)}
                title="Curate the magazine spread for this trip"
              >
                {magEditing ? '✓ Done magazine' : '✦ Edit magazine'}
              </button>
            )}
            {videos.length > 0 && (
              <button
                className={`cs-btn cs-btn-sm ${hlEditing ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                onClick={() => setHlEditing((e) => !e)}
                title="Curate the highlight clips for this trip"
              >
                {hlEditing ? '✓ Done highlights' : '★ Edit highlights'}
              </button>
            )}
            {!isTrashed(trip) && (
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                title={`Move this whole trip to trash · recoverable for ${TRASH_RETENTION_DAYS} days`}
                onClick={async () => {
                  if (window.confirm(`Move “${trip.name}” and all its media to the trash? You can restore it for ${TRASH_RETENTION_DAYS} days.`)) {
                    await trashTrip(trip.id);
                  }
                }}
              >
                🗑 Delete trip
              </button>
            )}
          </div>
        )}
      </div>

      {(cond || condStatus || Number.isFinite(trip.lat)) && (
        <div ref={condRef} className="cs-trip-overview-cond" style={{ scrollMarginTop: 16 }}>
          <SurfConditions
            cond={cond}
            status={condStatus}
            assets={active}
            selected={selectedAsset}
            tripStart={trip.startDate}
            tripEnd={trip.endDate}
            onSelectAsset={(a) => setSelId(a.id)}
            onClearAsset={() => setSelId(null)}
            onRefresh={canEdit ? () => requestTripConditions(id) : undefined}
            onSaveManual={canEdit ? (c) => saveManualConditions(id, c) : undefined}
            sessions={trip.surfSessions}
            onSaveSessions={canEdit ? (s) => setTripSurfSessions(id, s) : undefined}
          />
        </div>
      )}
      </div>

      {canEdit && showUpload && (
        <div className="cs-collapse-panel">
          <div className="cs-collapse-head">
            <h2 style={{ fontSize: 17, margin: 0 }}>Upload footage</h2>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setShowUpload(false)}>✕ Close</button>
          </div>
          <Uploader trip={trip} />
        </div>
      )}

      {canEdit && showDrop && (
        <div className="cs-collapse-panel">
          <div className="cs-collapse-head">
            <h2 style={{ fontSize: 17, margin: 0 }}>Share an upload link</h2>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setShowDrop(false)}>✕ Close</button>
          </div>
          <DropLinkManager trip={trip} />
        </div>
      )}

      {active.length > 0 && (magEditing || (trip.layout && (trip.layout.hero || (trip.layout.tiles || []).some(Boolean) || (trip.layout.carousel || []).length > 0))) && (
        <div style={{ marginTop: 16 }}>
          <TripFeature
            trip={trip}
            tripId={id}
            assets={active}
            onOpenPhoto={(u) => u && setLightbox(u)}
            editing={canEdit && magEditing}
            onEditingChange={setMagEditing}
          />
        </div>
      )}

      {(hlEditing || (trip.highlights || []).length > 0) && (
        <div style={{ marginTop: 16 }}>
          <TripHighlights trip={trip} tripId={id} assets={active} editing={canEdit && hlEditing} />
        </div>
      )}

      {tripMovies.length > 0 && (
        <div className="cs-movie-grid" style={{ marginTop: 16 }}>
          {tripMovies.map((m) => (
            <MovieCard key={m.id} movie={m} />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 88 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Media</h2>
        <span className="cs-help">Raw clips & photos — kept at full resolution</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {canEdit && trashed.length > 0 && (
            <button
              className={`cs-btn cs-btn-sm ${showTrash ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => setShowTrash((s) => !s)}
            >
              🗑 Trash ({trashed.length})
            </button>
          )}
          {canEdit && active.some((a) => a.s3Key) && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={dlAll} onClick={downloadAll}>
              {dlAll ? 'Downloading…' : '⤓ Download all'}
            </button>
          )}
          {photographers.length > 0 && (
            <select
              className="cs-input cs-input-sm"
              value={contribFilter}
              onChange={(e) => setContribFilter(e.target.value)}
              title="Filter media by photographer"
            >
              <option value="all">All photographers</option>
              {photographers.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
              {hasUntagged && <option value="__none">Unlabeled / mine</option>}
            </select>
          )}
          {surfers.length > 0 && (
            <select
              className="cs-input cs-input-sm"
              value={surferFilter}
              onChange={(e) => setSurferFilter(e.target.value)}
              title="Filter media by the surfer riding"
            >
              <option value="all">All surfers</option>
              {surfers.map((p) => (
                <option key={p.key} value={p.key}>🏄 {p.label}</option>
              ))}
              {hasUntaggedSurfer && <option value="__none">No surfer tagged</option>}
            </select>
          )}
          {canEdit && visible.length > 0 && (
            <ClockFix
              tripId={id}
              assets={visible}
              scopeLabel={
                contribFilter === 'all' && surferFilter === 'all'
                  ? 'all media shown'
                  : [
                      contribFilter === '__none' ? 'unlabeled media' : contribFilter !== 'all' ? selectedPhotographer : '',
                      surferFilter === '__none' ? 'no surfer tagged' : surferFilter !== 'all' ? `🏄 ${selectedSurfer}` : '',
                    ].filter(Boolean).join(' · ')
              }
            />
          )}
          <div className="cs-segment">
            <button className={group === 'day' ? 'on' : ''} onClick={() => setGroup('day')}>By day</button>
            <button className={group === 'type' ? 'on' : ''} onClick={() => setGroup('type')}>By type</button>
          </div>
        </div>
      </div>

      {canEdit && <UploadingNow tripId={id} assets={assets} />}

      {assets === null ? (
        <div style={{ marginTop: 12 }}>Loading…</div>
      ) : active.length === 0 ? (
        <div className="cs-empty cs-media-empty" style={{ marginTop: 12 }}>
          <div>No footage uploaded yet.</div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
              <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => setShowUpload(true)}>
                ⬆ Upload footage
              </button>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setShowDrop(true)}>
                🔗 Share an upload link
              </button>
            </div>
          )}
        </div>
      ) : visible.length === 0 ? (
        <div className="cs-empty cs-media-empty" style={{ marginTop: 12 }}>
          <div>
            No media matching{' '}
            <strong>
              {[
                contribFilter === '__none' ? 'unlabeled uploads' : contribFilter !== 'all' ? selectedPhotographer : '',
                surferFilter === '__none' ? 'no surfer tagged' : surferFilter !== 'all' ? `surfer ${selectedSurfer}` : '',
              ].filter(Boolean).join(' + ')}
            </strong>.
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => { setContribFilter('all'); setSurferFilter('all'); }}
            >
              Clear filters
            </button>
          </div>
        </div>
      ) : group === 'day' ? (
        days.map(([label, items]) => (
          <section key={label} style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 10 }}>
              {label} · {items.length} {items.length === 1 ? 'item' : 'items'}
            </h3>
            <div className="cs-grid">{items.map(renderAsset)}</div>
          </section>
        ))
      ) : (
        <>
          {videos.length > 0 && (
            <section style={{ marginTop: 20 }}>
              <h3 style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 10 }}>Clips · {videos.length}</h3>
              <div className="cs-grid">{videos.map(renderAsset)}</div>
            </section>
          )}
          {photos.length > 0 && (
            <section style={{ marginTop: 20 }}>
              <h3 style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 10 }}>Photos · {photos.length}</h3>
              <div className="cs-grid">{photos.map(renderAsset)}</div>
            </section>
          )}
        </>
      )}

      {canEdit && showTrash && trashed.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <h3 style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 10 }}>
            🗑 Trash · {trashed.length} — recoverable for {TRASH_RETENTION_DAYS} days
          </h3>
          <div className="cs-grid">{trashed.map(renderAsset)}</div>
        </section>
      )}

      {vaporizeTripOpen && (
        <VaporizeModal
          what={`the trip “${trip.name}” and ALL of its media`}
          onConfirm={async () => {
            await vaporizeTrip(trip);
            navigate('/trips');
          }}
          onClose={() => setVaporizeTripOpen(false)}
        />
      )}

      {lightbox && (
        <div
          className="cs-lightbox-full"
          role="button"
          tabIndex={0}
          onClick={() => setLightbox(null)}
          onKeyDown={(e) => (e.key === 'Escape' || e.key === 'Enter') && setLightbox(null)}
        >
          <img src={lightbox} alt="" decoding="async" />
          <button
            type="button"
            className="cs-lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}

      {editing && (
        <TripEditModal trip={trip} videos={videos} onClose={() => setEditing(false)} />
      )}

      {grabAsset && (
        <FrameGrabber asset={grabAsset} tripId={id} onClose={() => setGrabAsset(null)} />
      )}
    </div>
  );
}
